import apiClient from '../index';

/**
 * Create a new enrollment
 * @param {Object} enrollmentData - Data for the new enrollment
 * @returns {Promise<Object>} The created enrollment
 */
export const createEnrollment = async (enrollmentData) => {
  try {
    const response = await apiClient.post('/api/enrollment/', enrollmentData);
    return response.data;
  } catch (error) {
    console.error(
      'Error creating enrollment:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get all enrollments
 * @returns {Promise<Array>} List of all enrollments
 */
export const getAllEnrollments = async () => {
  try {
    const response = await apiClient.get('/api/enrollment/');
    return response.data;
  } catch (error) {
    console.error(
      'Error fetching all enrollments:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get all enrollment groups
 * @returns {Promise<Array>} List of enrollment groups
 */
export const getEnrollmentGroups = async () => {
  try {
    const response = await apiClient.get('/api/enrollment/groups');
    return response.data;
  } catch (error) {
    console.error(
      'Error fetching enrollment groups:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get enrollments for a specific child
 * @param {string} childId - The ID of the child
 * @returns {Promise<Array>} List of enrollments for the child
 */
export const getChildEnrollments = async (childId) => {
  if (!childId) throw new Error('Child ID is required');

  try {
    const response = await apiClient.get(`/api/enrollment/child/${childId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching enrollments for child ID ${childId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get a specific enrollment by ID
 * @param {string} enrollmentId - The ID of the enrollment
 * @returns {Promise<Object>} The enrollment details
 */
export const getEnrollmentById = async (enrollmentId) => {
  if (!enrollmentId) throw new Error('Enrollment ID is required');

  try {
    const response = await apiClient.get(`/api/enrollment/${enrollmentId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching enrollment with ID ${enrollmentId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get enrollments by class ID
 * @param {string} classId - The ID of the class
 * @returns {Promise<Array>} List of enrollments for the class
 */
export const getEnrollmentsByClassId = async (classId) => {
  if (!classId) throw new Error('Class ID is required');

  try {
    const response = await apiClient.get(`/api/enrollment/class/${classId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching enrollments for class ID ${classId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Update an existing enrollment
 * @param {string} enrollmentId - The ID of the enrollment to update
 * @param {Object} data - The updated enrollment data
 * @returns {Promise<Object>} The updated enrollment
 */
export const updateEnrollment = async (enrollmentId, data) => {
  if (!enrollmentId) throw new Error('Enrollment ID is required');
  if (!data) throw new Error('Update data is required');

  try {
    console.log('Updating enrollment with ID:', enrollmentId);
    console.log('Update payload:', data);

    const response = await apiClient.put(
      `/api/enrollment/${enrollmentId}`,
      data
    );

    console.log('Update response:', response.data);

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return Array.isArray(response.data) ? response.data[0] : response.data;
  } catch (error) {
    console.error('Enrollment update error:', {
      enrollmentId,
      error: error.response?.data || error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });

    if (error.response?.status === 404) {
      throw new Error(`Enrollment not found (ID: ${enrollmentId})`);
    }
    throw error;
  }
};

/**
 * Delete an enrollment
 * @param {string} enrollmentId - The ID of the enrollment to delete
 * @returns {Promise<Object>} The deletion confirmation
 */
export const deleteEnrollment = async (enrollmentId) => {
  if (!enrollmentId) throw new Error('Enrollment ID is required');

  try {
    const response = await apiClient.delete(`/api/enrollment/${enrollmentId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Enrollment not found');
    }
    console.error(
      `Error deleting enrollment with ID ${enrollmentId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};
